<template>
  <Tooltip
    v-if="tooltip"
    class="inline"
    :triggers="['hover', 'click', 'focus']"
    @apply-hide="restoreDescription"
  >
    <template #popper>
      <div class="max-w-150 p-3 whitespace-pre-line">
        {{ tooltip }}
      </div>
    </template>
    <template #default="{ shown, hide }">
      <!-- Only an opted-in trigger gets a box of its own; every other call
           site renders the bare icon it rendered before this control grew a
           focus affordance, since none of them can be focused.

           Four things a future editor can break:

           - `v-bind="$attrs"` must stay last: Vue's merge is later-wins, and
             with it first a caller's `tabindex` or `aria-label` was silently
             dropped. `class` and `on*` merge either way, so the Escape
             binding survives a caller's own `@keydown`.
           - An opt-in must not sit inside a `<button>` or `<a>` - the trigger
             is itself a button, so that is a tab stop inside a tab stop and
             invalid HTML - nor inside anything named from its contents
             (`<h1>`-`<h6>`, `<label>`, `<th>`), which would swallow the loose
             text of the description. The dev warning below catches both.
           - The description stays a *sibling*: ARIA makes a `button`'s
             children presentational, so AT may prune the subtree and resolve
             a description nested there to nothing. It exists at all because
             floating-vue points `aria-describedby` at the popper only from
             inside its show effect, 200ms after focus - too late for a screen
             reader that has computed the description already.
           - It is also a second floating-vue target node, so it carries the
             popper's listeners and attributes too. Harmless at 1px and
             clipped, but it is why this trigger has a twin and the bare icon
             does not.

           The button's classes, briefly: `align-middle` restores the
           `vertical-align` preflight gives the svg and inline-flex discards;
           `p-0.5` keeps the indicator off the glyph's own circular outline;
           `outline` rather than a ring paints in forced colours; `purple-800`
           clears WCAG 1.4.11's 3:1; `focus-visible` spares mouse users a
           lingering indicator; `cursor-pointer` because Tailwind v4's
           preflight no longer sets it on `button`. -->
      <template v-if="focusable">
        <button
          ref="triggerEl"
          type="button"
          aria-label="More information"
          :aria-describedby="describedBy"
          class="inline-flex cursor-pointer rounded-full p-0.5 align-middle text-gray-600 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-purple-800"
          v-bind="$attrs"
          @keydown.esc="dismiss(shown, hide, $event)"
        >
          <Icon name="question-circle" aria-hidden="true" class="size-3" />
        </button>
        <span :id="descriptionId" class="sr-only">
          {{ tooltip }}
        </span>
      </template>
      <Icon
        v-else
        v-bind="$attrs"
        name="question-circle"
        aria-hidden="true"
        class="inline size-3 rounded-full text-gray-600"
      />
    </template>
  </Tooltip>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, useAttrs, useId } from "vue";
import Icon from "@/components/Core/Icon.vue";
import { Tooltip } from "floating-vue";

defineProps({
  tooltip: { type: String, default: "" },
  focusable: { type: Boolean, default: false },
});

// useId, not a random string: the id must be stable across re-renders or
// aria-describedby points at an element that no longer exists.
const descriptionId = useId();
const triggerEl = ref<HTMLElement | null>(null);
const attrs = useAttrs();

// Whatever won the `$attrs` merge, not necessarily our own id: a call site may
// point the trigger at a description of its own, and restoring ours would
// silently take that back on the first hide.
const describedBy = computed(() => {
  const override = attrs["aria-describedby"];
  return typeof override === "string" ? override : descriptionId;
});

// floating-vue writes its own `aria-describedby` onto the trigger while the
// popper is shown and *removes the attribute* when it hides, taking ours with
// it. Vue will not put it back — the bound value never changed — so the
// second focus onwards would have no description at all.
function restoreDescription() {
  triggerEl.value?.setAttribute("aria-describedby", describedBy.value);
}

// WCAG 1.4.13 wants focus-shown content dismissible without moving focus, and
// floating-vue cannot do it here: its only Esc handler is on the popper node,
// behind `autoHide`, which the `tooltip` theme leaves unset — so the popper
// gets no tabindex and the keyup never reaches the handler that is a no-op
// anyway. Hiding from the trigger leaves focus where it is, and no fresh
// `focus` or `mouseenter` fires while it stays there; Enter or Space on the
// button brings it back, since `click` is a show trigger.
function dismiss(
  shown: boolean,
  hide: (options?: { skipDelay?: boolean }) => void,
  event: KeyboardEvent,
) {
  if (shown) {
    // Headless UI's dialog closes on a window-level Escape unless the event is
    // already defaultPrevented, so a tooltip inside a modal dismisses on the
    // first press and the modal closes on the second. With nothing shown there
    // is nothing to dismiss, so the key belongs to the dialog.
    event.preventDefault();
  }
  // Unconditional: inside the theme's 200ms show delay `shown` is still false
  // while a show is already scheduled, and hiding is what cancels it.
  // `$_applyHide` early-returns when there is nothing shown to hide.
  hide({ skipDelay: true });
}

const LEAKY_ANCESTORS =
  "h1,h2,h3,h4,h5,h6,label,th,button,a[href],summary,legend,caption";

// Both ways an opt-in goes wrong live in the call site's DOM, so neither shows
// up in a test that mounts this component on its own. Mounting is the first
// moment that DOM exists.
onMounted(() => {
  if (!import.meta.env.DEV || !triggerEl.value) {
    return;
  }
  const ancestor = triggerEl.value.parentElement?.closest(LEAKY_ANCESTORS);
  if (ancestor) {
    console.warn(
      `[TooltipInfoIcon] focusable trigger mounted inside <${ancestor.tagName.toLowerCase()}>. ` +
        "Its description is folded into that element's accessible name, and a " +
        "<button>/<a> ancestor also nests a tab stop inside a tab stop. Move " +
        "the trigger out of it, or drop `focusable`.",
    );
  }
});

defineOptions({
  inheritAttrs: false,
});
</script>
